import Logo from '@/components/base/Logo';

export interface IdEntryPageProps {
  userId: string;
  onUserIdChange: (value: string) => void;
  isLoading: boolean;
  errorMsg: string;
  focusedField: boolean;
  onFocusChange: (focused: boolean) => void;
  onSubmit: (e: React.FormEvent) => void;
}

export default function IdEntryPage({
  userId,
  onUserIdChange,
  isLoading,
  errorMsg,
  focusedField,
  onFocusChange,
  onSubmit,
}: IdEntryPageProps) {
  return (
    <div className="min-h-screen bg-primary-50/40 flex flex-col items-center justify-center px-5 py-10">
      <div className="w-full max-w-sm">
        {/* Header */}
        <Logo
          icon="ri-graduation-cap-line"
          title="꿈을 Pick! 나를 Up!"
          subtitle="아이디를 입력하고 내 마일리지를 확인하세요"
          className="mb-6"
        />

        <div className="bg-background-50 rounded-2xl border-2 border-primary-100 p-6">
          <form onSubmit={onSubmit} className="space-y-4">
            {/* ID Input */}
            <div>
              <label htmlFor="learner-id" className="block text-sm font-medium text-foreground-700 mb-1.5">
                아이디
              </label>
              <div
                className={`flex items-center gap-3 px-4 py-3 rounded-xl border-2 transition-colors bg-background-50 ${
                  focusedField
                    ? 'border-primary-400 ring-4 ring-primary-100/50'
                    : 'border-background-200'
                }`}
              >
                <i className={`ri-user-line text-lg ${focusedField ? 'text-primary-500' : 'text-foreground-400'}`}></i>
                <input
                  id="learner-id"
                  type="text"
                  value={userId}
                  onChange={(e) => onUserIdChange(e.target.value)}
                  onFocus={() => onFocusChange(true)}
                  onBlur={() => onFocusChange(false)}
                  placeholder="아이디를 입력하세요"
                  autoComplete="username"
                  className="flex-1 bg-transparent text-sm text-foreground-950 placeholder:text-foreground-400 outline-none"
                />
                {userId && (
                  <button
                    type="button"
                    onClick={() => onUserIdChange('')}
                    className="w-5 h-5 flex items-center justify-center rounded-full bg-secondary-200 hover:bg-secondary-300 transition-colors cursor-pointer shrink-0"
                  >
                    <i className="ri-close-line text-xs text-foreground-500"></i>
                  </button>
                )}
              </div>
            </div>

            {/* Error Message */}
            {errorMsg && (
              <div className="flex items-center gap-3 px-4 py-3.5 rounded-xl bg-red-50 border border-red-100">
                <div className="w-7 h-7 shrink-0 rounded-full bg-red-100 flex items-center justify-center">
                  <i className="ri-error-warning-fill text-sm text-red-500"></i>
                </div>
                <p className="text-sm text-red-600 font-medium leading-relaxed">{errorMsg}</p>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={userId.trim() === '' || isLoading}
              className={`w-full py-3.5 rounded-xl text-base font-semibold transition-all duration-200 whitespace-nowrap cursor-pointer ${
                userId.trim() !== '' && !isLoading
                  ? 'bg-primary-500 text-background-50 hover:bg-primary-600 active:scale-[0.98]'
                  : 'bg-secondary-200 text-secondary-400 cursor-not-allowed'
              }`}
            >
              {isLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <i className="ri-loader-4-line animate-spin text-lg"></i>
                  조회 중...
                </span>
              ) : (
                '조회'
              )}
            </button>
          </form>
        </div>

        {/* Secondary action */}
        <div className="flex items-center justify-center mt-5">
          <a
            href="/course-recommend"
            className="flex items-center gap-1.5 text-sm text-foreground-500 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            콘텐츠 추천 진단 먼저 해보기
            <i className="ri-arrow-right-line text-base"></i>
          </a>
        </div>
      </div>
    </div>
  );
}
